package com.exura.components.layouts.plan

import com.exura.ChartsRawPastelPalette
import com.exura.primary
import com.exura.surface
import com.exura.transparent
import com.exura.ui.Dimens
import com.exura.ui.Durations
import com.varabyte.kobweb.compose.css.ColorInterpolationMethod
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.colorMix
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderRight
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.overlay
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.times

val PlanConstructorHeaderStyle = CssStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .fillMaxWidth()
            .padding(leftRight = Dimens.planConstructorHeaderIndent, topBottom = Dimens.planConstructorHeaderWrapping)
            .borderBottom(
                width = Dimens.planConstructorHeaderSeparator,
                style = LineStyle.Solid,
                color = palette.border,
            )
    }
}

sealed interface PlanConstructorHeaderElementKind : ComponentKind
val PlanConstructorHeaderElementStyle = CssStyle<PlanConstructorHeaderElementKind> {
    base {
        Modifier
            .fillMaxHeight()
            .padding(
                leftRight = Dimens.planConstructorHeaderElementIndent,
                topBottom = Dimens.planConstructorHeaderElementWrapping,
            )
    }
}
val PlanConstructorHeaderElementBorderedVariant = PlanConstructorHeaderElementStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderRight(
                width = Dimens.planConstructorHeaderElementSeparator,
                style = LineStyle.Solid,
                color = palette.border,
            )
    }
}

sealed interface SideBarKind: ComponentKind
val SideBarStyle = CssStyle<SideBarKind> {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .fillMaxHeight()
            .fillMaxWidth()
            .borderRight(
                width = Dimens.planConstructorSideBarSideSeparator,
                style = LineStyle.Solid,
                color = palette.border,
            )
    }
}
val SideBarCompactVariant = SideBarStyle.addVariant {
    base {
        Modifier.maxWidth(Dimens.planConstructorSideBarElementListMaxWidth)
    }
}

sealed interface SideBarRowKind : ComponentKind
val rowFlavorColor by StyleVariable<CSSColorValue>(ChartsRawPastelPalette.Grey)
val SideBarRowStyle = CssStyle<SideBarRowKind> {
    base {
        Modifier
            .fillMaxWidth()
    }
}
val SideBarRowHoverableVariant = SideBarRowStyle.addVariant {
    val palette = colorMode.toPalette()
    hover {
        Modifier
            .backgroundColor(palette.surface)
            .fontWeight(FontWeight.Medium)
    }
}
val SideBarRowSeparatedVariant = SideBarRowStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderBottom(
                width = Dimens.planConstructorSideBarElementsSeparator,
                style = LineStyle.Solid,
                color = palette.border
            )
    }
}
val SideBarRowSeparatedThickVariant = SideBarRowStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderBottom(
                width = Dimens.planConstructorSideBarSideSeparator,
                style = LineStyle.Solid,
                color = palette.border
            )
    }
}
val SideBarRowSelectedVariant = SideBarRowStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .backgroundColor(palette.surface)
            .fontWeight(FontWeight.Medium)
    }
    hover {
        Modifier
            .backgroundColor(palette.surface)
            .fontWeight(FontWeight.Bold)
    }
}
val SideBarRowTealVariant = SideBarRowStyle.addVariant {
    base {
        Modifier.setVariable(rowFlavorColor, ChartsRawPastelPalette.Teal)
    }
}
val SideBarRowRedVariant = SideBarRowStyle.addVariant {
    base {
        Modifier.setVariable(rowFlavorColor, ChartsRawPastelPalette.Red)
    }
}
val SideBarRowBlueVariant = SideBarRowStyle.addVariant {
    base {
        Modifier.setVariable(rowFlavorColor, ChartsRawPastelPalette.Blue)
    }
}
val SideBarRowOrangeVariant = SideBarRowStyle.addVariant {
    base {
        Modifier.setVariable(rowFlavorColor, ChartsRawPastelPalette.Orange)
    }
}

sealed interface SideBarRowContentKind : ComponentKind
val SideBarRowContentStyle = CssStyle<SideBarRowContentKind> {
    base {
        Modifier
            .fillMaxWidth()
            .transition(Transition.all(Durations.short))
    }
}
val SideBarRowContentListVariant = SideBarRowContentStyle.addVariant {
    base {
        Modifier
            .minWidth(Dimens.planConstructorSideBarElementListMaxWidth)
            .width(Dimens.planConstructorSideBarElementListMaxWidth)
            .padding(
                leftRight = Dimens.planConstructorSideBarElementIndent,
                topBottom = Dimens.planConstructorSideBarElementWrapping,
            )
    }
}
val SideBarRowContentNestedVariant = SideBarRowContentStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .backgroundColor(palette.overlay.toRgb().copyf(alpha = 0.05f))
    }
}
val SideBarRowContentIndentVariant = SideBarRowContentStyle.addVariant {
    base {
        Modifier
            .padding(
                left = 3 * Dimens.planConstructorSideBarElementIndent,
                right = Dimens.planConstructorSideBarElementIndent,
                top = Dimens.planConstructorSideBarElementWrapping,
                bottom = Dimens.planConstructorSideBarElementWrapping,
            )
    }
}
val SideBarRowContentGanttVariant = SideBarRowContentStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderLeft(
                width = Dimens.planConstructorSideBarSideSeparator,
                style = LineStyle.Solid,
                color = palette.border,
            )
            .padding(
                leftRight = Dimens.planConstructorSideBarElementIndent,
            )
    }
}

val SideBarRowContentHiddenVariant = SideBarRowContentStyle.addVariant {
    base {
        Modifier
            .width(Dimens.spacingNone)
            .maxWidth(Dimens.spacingNone)
            .padding(Dimens.spacingNone)
            .overflow(Overflow.Hidden)
            .opacity(0)
    }
}
val SideBarRowContentCollectionHeightVariant = SideBarRowContentStyle.addVariant {
    base {
        Modifier
            .height(Dimens.planConstructorSideBarElementCollectionHeight)
            .minHeight(Dimens.planConstructorSideBarElementCollectionHeight)
    }
}
val SideBarRowContentSeriesHeightVariant = SideBarRowContentStyle.addVariant {
    base {
        Modifier
            .height(Dimens.planConstructorSideBarElementSeriesHeight)
            .minHeight(Dimens.planConstructorSideBarElementSeriesHeight)
    }
}
val SideBarRowContentHeaderHeightVariant = SideBarRowContentStyle.addVariant {
    base {
        Modifier
            .height(Dimens.planConstructorSideBarElementHeaderHeight)
            .minHeight(Dimens.planConstructorSideBarElementHeaderHeight)
            .padding(Dimens.spacingNone)
    }
}

sealed interface GantDayBoxKind : ComponentKind
val GantDayBoxStyle = CssStyle<GantDayBoxKind> {
    base {
        Modifier
            .minWidth(Dimens.planConstructorGanttChartDayBoxMinWidth)
            .fillMaxWidth()
            .fillMaxHeight()
            .flexGrow(9)
            .padding(
                top = Dimens.planConstructorSideBarElementWrapping,
                bottom = Dimens.planConstructorSideBarElementWrapping,
            )
    }
}

val GantDayBoxStartingVariant = GantDayBoxStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderLeft(
                width = Dimens.planConstructorGanttChartDaySeparatorWidth,
                style = LineStyle.Dashed,
                color = palette.border,
            )
            .margin(
                right = -1 * Dimens.planConstructorGanttChartDaySeparatorWidth,
            )
    }
}
val GantDayBoxEndingVariant = GantDayBoxStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderRight(
                width = Dimens.planConstructorGanttChartDaySeparatorWidth,
                style = LineStyle.Dashed,
                color = palette.border,
            )
    }
}
val GantDayBoxPlanAnchorStartVariant = GantDayBoxStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderLeft(
                width = Dimens.planConstructorGanttChartDaySeparatorWidth,
                style = LineStyle.Dashed,
                color = palette.primary,
            )
    }
}
val GantDayBoxPlanAnchorEndVariant = GantDayBoxStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderRight(
                width = Dimens.planConstructorGanttChartDaySeparatorWidth,
                style = LineStyle.Dashed,
                color = palette.primary,
            )
    }
}
sealed interface PlanConstructorSideBarDayBoxKind : ComponentKind
val dayBoxSpawnedChildDefaultColor by StyleVariable<CSSColorValue>()
val PlanConstructorSideBarDayBoxStyle = CssStyle<PlanConstructorSideBarDayBoxKind> {
    base {
        Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .padding(Dimens.planConstructorSpawnedElementWrapping)
            .setVariable(dayBoxSpawnedChildDefaultColor, rowFlavorColor.value())
    }
}
val PlanConstructorSideBarDayBoxMiddleVariant = PlanConstructorSideBarDayBoxStyle.addVariant {
    base {
        Modifier
            .borderTop(
                width = Dimens.planConstructorGanttChartDayBoxBorderWidth,
                style = LineStyle.Solid,
                color = rowFlavorColor.value(),
            )
            .borderBottom(
                width = Dimens.planConstructorGanttChartDayBoxBorderWidth,
                style = LineStyle.Solid,
                color = rowFlavorColor.value(),
            )
    }
}
val PlanConstructorSideBarDayBoxStartVariant = PlanConstructorSideBarDayBoxStyle.addVariant {
    base {
        Modifier
            .borderLeft(
                width = Dimens.planConstructorGanttChartDayBoxBorderWidth,
                style = LineStyle.Solid,
                color = rowFlavorColor.value(),
            )
            .borderRadius(
                topLeft = Dimens.planConstructorGanttChartDayBoxBorderRadius,
                bottomLeft = Dimens.planConstructorGanttChartDayBoxBorderRadius,
            )
    }
}
val PlanConstructorSideBarDayBoxEndVariant = PlanConstructorSideBarDayBoxStyle.addVariant {
    base {
        Modifier
            .borderRight(
                width = Dimens.planConstructorGanttChartDayBoxBorderWidth,
                style = LineStyle.Solid,
                color = rowFlavorColor.value(),
            )
            .borderRadius(
                topRight = Dimens.planConstructorGanttChartDayBoxBorderRadius,
                bottomRight = Dimens.planConstructorGanttChartDayBoxBorderRadius,
            )
    }
}
val PlanConstructorSideBarDayBoxSingleDayVariant = PlanConstructorSideBarDayBoxStyle.addVariant {
    base {
        Modifier
            .border(
                width = Dimens.planConstructorGanttChartDayBoxBorderWidth,
                style = LineStyle.Solid,
                color = rowFlavorColor.value(),
            )
            .borderRadius(Dimens.planConstructorGanttChartDayBoxBorderRadius)
    }
}
val PlanConstructorSideBarDayBoxHoverableVariant = PlanConstructorSideBarDayBoxStyle.addVariant {
    val palette = colorMode.toPalette()
    hover {
        Modifier
            .backgroundColor(rowFlavorColor.value())
            .setVariable(dayBoxSpawnedChildDefaultColor, palette.surface)
    }
}
val PlanConstructorSideBarDayBoxFilledVariant = PlanConstructorSideBarDayBoxStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .backgroundColor(rowFlavorColor.value())
            .setVariable(dayBoxSpawnedChildDefaultColor, palette.surface)
    }
}

sealed interface DayBoxSeriesElementKind : ComponentKind
val DayBoxSeriesElementStyle = CssStyle<DayBoxSeriesElementKind> {
    base {
        Modifier
            .aspectRatio(1)
            .flexGrow(0)
            .flexShrink(0)
            .borderRadius(50.percent)
            .fillMaxHeight()
            .backgroundColor(dayBoxSpawnedChildDefaultColor.value())
    }
}
val DayBoxSeriesElementFilledVariant = DayBoxSeriesElementStyle.addVariant {
    base {
        Modifier
            .backgroundColor(rowFlavorColor.value())
    }
}

val SpawnedElementTooltipStyle = CssStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .minWidth(Dimens.planConstructorTooltipMinWidth)
            .maxWidth(Dimens.planConstructorTooltipMaxWidth)
            .padding(
                leftRight = Dimens.planConstructorTooltipIndent,
                topBottom = Dimens.planConstructorTooltipWrapping,
            )
            .backgroundColor(palette.surface)
            .color(palette.color)
            .borderRadius(Dimens.planConstructorTooltipBorderRadius)
    }
}
val constructorSeriesIconStyle = CssStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .color(rowFlavorColor.value())
            .backgroundColor(
                colorMix(
                    ColorInterpolationMethod.Srgb,
                    rowFlavorColor.value(),
                    palette.transparent to 90.percent,
                )
            )
            .padding(Dimens.planConstructorSeriesIconPadding)
            .borderRadius(Dimens.planConstructorSeriesIconBorderRadius)
    }
}